import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, LogIn, LogOut, Menu, RefreshCw, UserCog } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantSettings } from '../../contexts/TenantSettingsContext';
import { NotificationsBell } from './NotificationsBell';

/**
 * Header applicatif (US-DASH-03 + US-TR-10).
 *
 *  - Burger mobile (déclenche l'ouverture sidebar via prop).
 *  - Logo / nom du tenant courant (ou "Plateforme" pour super-admin).
 *  - Cloche notifications avec badge live + dropdown (US-NOTIF-02).
 *  - Dropdown profil : avatar + nom → liens `/profil` + déconnexion.
 *  - Bandeau impersonation (jaune) au-dessus du header : visible quand
 *    `user.is_impersonated === true`. Bouton "Revenir admin plateforme"
 *    appelle `endImpersonation()` du contexte auth.
 */

interface HeaderProps {
  onMobileMenu(): void;
}

export function Header({ onMobileMenu }: HeaderProps) {
  const { user, logout, endImpersonation } = useAuth();
  const { tenant: tenantSettings } = useTenantSettings();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [exiting, setExiting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (!user) return null;

  const tenantLabel = user.tenant ? user.tenant.nom : user.is_platform_admin ? 'Plateforme' : '—';

  const initials = `${user.first_name[0] ?? ''}${user.last_name[0] ?? ''}`.toUpperCase();
  const isImpersonating = Boolean(user.is_impersonated);

  async function handleEndImpersonation() {
    setExiting(true);
    try {
      await endImpersonation();
      navigate('/platform/tenants');
    } catch {
      setExiting(false);
    }
  }

  return (
    <>
      {isImpersonating && (
        <div className="fixed top-0 right-0 left-0 lg:left-16 z-30 bg-yellow-100 border-b border-yellow-300 text-yellow-900 px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm flex items-center font-medium">
            <RefreshCw className="w-4 h-4 mr-2" />
            <span>
              Vue Tenant&nbsp;: <strong>{user.tenant?.nom ?? user.tenant?.code ?? '—'}</strong>
            </span>
            <span className="ml-2 text-xs font-normal text-yellow-800">
              (session impersonifiée — connecté en tant que {user.first_name} {user.last_name})
            </span>
          </p>
          <button
            type="button"
            onClick={handleEndImpersonation}
            disabled={exiting}
            className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-60"
          >
            <LogIn className="w-3.5 h-3.5 mr-1.5 rotate-180" />
            {exiting ? 'Sortie…' : 'Revenir admin plateforme'}
          </button>
        </div>
      )}

      <header
        className={`fixed right-0 left-0 lg:left-16 z-20 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 shadow-sm ${
          isImpersonating ? 'top-10 sm:top-9' : 'top-0'
        }`}
      >
        <div className="flex items-center space-x-3">
          <button
            onClick={onMobileMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            aria-label="Ouvrir le menu"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center space-x-2">
            {tenantSettings?.logo_signed_url ? (
              <img
                src={tenantSettings.logo_signed_url}
                alt=""
                className="w-7 h-7 rounded-md object-contain bg-white"
              />
            ) : (
              <div className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold">
                {user.tenant?.code?.slice(0, 2).toUpperCase() ?? 'PL'}
              </div>
            )}
            <span className="text-sm font-medium text-gray-900 hidden sm:inline">
              {tenantLabel}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Cloche notifications + dropdown (US-NOTIF-02). Cachée pour les
              super-admins plateforme (qui ne reçoivent pas de notifications). */}
          {!user.is_platform_admin && <NotificationsBell />}

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((s) => !s)}
              className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100"
            >
              {user.avatar_signed_url ? (
                <img
                  src={user.avatar_signed_url}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold">
                  {initials || '?'}
                </div>
              )}
              <span className="text-sm font-medium text-gray-900 hidden sm:inline">
                {user.first_name}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white border border-gray-200 shadow-lg py-1">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <Link
                  to="/profil"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <UserCog className="w-4 h-4 mr-2 text-gray-500" />
                  Mon profil
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="w-full text-left flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <LogOut className="w-4 h-4 mr-2 text-gray-500" />
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
